import { Formik, Form } from "formik";
import { FormModel } from "../types/FormTypes";
import { useAtom } from "jotai";
import { formDataAtom } from "../App";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useEffect, useRef } from "react";
import useFocusOnLoad from "../hooks/useFocusOnLoad";
import { COMPENSATION_CAR } from "./Compensation";

import {
  Box,
  Button,
  handleSubmitClick,
  ErrorList,
  Progressbar,
  InputText,
  MultipleFileUploadField,
  Heading,
} from "@ogt/ui";

export default function CompensationForCar() {
  const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
  const [formData, setFormData] = useAtom(formDataAtom);
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const progress = 60;
  // Reset specific fields if the selected compensation is not COMPENSATION_CAR
  useEffect(() => {
    if (formData.compensation === COMPENSATION_CAR) {
      setFormData((prev) => ({
        ...prev,
        taxireceipt: [],
      }));
    }
  }, [formData.compensation, setFormData]);

  return (
    <div>
      <Formik<FormModel>
        onSubmit={(values) => {
          setFormData({
            ...formData,
            ...values,
          });

          navigate("/hur-vill-du-ha-ersattning");
        }}
        initialValues={{
          ...formData,
        }}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={Yup.object().shape({
          cartravelfrom: Yup.string().required(
            "Vänligen fyll i vart du åkte från"
          ),
          cartravelto: Yup.string().required(
            "Vänligen fyll i vart du åkte till"
          ),
          distance: Yup.string()
            .required("Vänligen fyll i hur långt du åkte")
            .max(10, "Antalet tecken får vara högst 10"),
          carlicenceplate: Yup.string().required(
            "Vänligen fyll din bils registreringsnummer"
          ),
        })}
      >
        {({ errors, values }) => (
          <>
            <div className="flex justify-end items-center">
              <div className="w-full max-w-xs mb-4">
                <Progressbar percentage={progress} />
              </div>
            </div>
            <Form noValidate>
              <Box>
                <Heading progress={progress} headingRef={h2Ref} focusable>
                  Resa med bil
                </Heading>

                <p>
                  Fyll i uppgifterna om din resa. Har du behövt betala för
                  parkering bifogar du kvitto för det (bild på kvittot räcker).
                  Du kan få ersättning upp till 1/40-del av gällande
                  prisbasbelopp.
                </p>
                {Object.keys(errors).length > 0 && (
                  <ErrorList errors={errors} />
                )}

                <InputText
                  name="cartravelfrom"
                  label={
                    <>
                      Jag åkte från <span>(Obligatoriskt)</span>
                    </>
                  }
                  type="text"
                  autocomplete={"off"}
                />

                <InputText
                  name="cartravelto"
                  label={
                    <>
                      Jag åkte till <span>(Obligatoriskt)</span>
                    </>
                  }
                  type="text"
                  autocomplete={"off"}
                />

                <InputText
                  name="distance"
                  label={
                    <>
                      Antal mil <span>(Obligatoriskt)</span>
                    </>
                  }
                  type="text"
                  numbersAndDecimalsOnly
                  autocomplete={"off"}
                />

                <InputText
                  name="carlicenceplate"
                  label={
                    <>
                      Registreringsnummer <span>(Obligatoriskt)</span>
                    </>
                  }
                  type="text"
                  autocomplete={"off"}
                  upperCase
                />

                <MultipleFileUploadField
                  currentFiles={values.carexpenses}
                  multiple={true}
                  type="Filer"
                  label="Bifoga underlag för övriga kostnader"
                  inputId="carexpenses"
                  inputName="carexpenses"
                />
              </Box>

              <div className="flex justify-center">
                <div className="w-full text-left flex items-center">
                  <Button
                    iconPosition="left"
                    iconSize={16}
                    iconName="arrow-left"
                    aria-label="Tillbaka"
                    showIcon
                    size="medium"
                    variant="outline"
                    onClick={(event) => {
                      event.preventDefault();
                      const backUrl = undefined;
                      if (backUrl) {
                        navigate(backUrl, { replace: true });
                      } else {
                        navigate(-1);
                      }
                    }}
                  >
                    Tillbaka
                  </Button>
                </div>
                <div className="w-full text-right">
                  <Button
                    onClick={handleSubmitClick(formRef)}
                    className="btn primary"
                    type="submit"
                  >
                    Gå vidare
                  </Button>
                </div>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
}
